import './AreaIndicatorMiniCard.css';

import React, { useState } from 'react';
import { Box, Card, CardContent, CardHeader } from '@mui/material';
import PlanAreaPath from '../PlanAreaPath';
import { WeightRatingField } from './WeightRating';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';
import ProgressFactory from '../progress/ProgressFactory';
import StatusTrendFactory from '../status-trend/StatusTrendFactory';
import IndicatorImpact from '../../../b2b-frameworks/components/framework-impact/IndicatorImpact';
import { useNavigate } from 'react-router-dom';
import { generateClassName } from '../../../b2b-common/helpers';
import { calculateTimeProgress, getCurrentObjective } from '../helper';
import MoreButton from '../../../b2b-common/MoreButton';
import { MoreVert } from '@mui/icons-material';
import AreaIndicatorLastSample from './AreaIndicatorLastSample';
import IndicatorUnitFactory from '../../../b2b-indicators/components/units/IndicatorUnitFactory';

const AreaIndicatorMiniCard = ({ areaIndicator, showCalendarProgress = false, showTargetProgress = true,
    allowClickOnName = true,
    allowClickOnCard = false,
    highlightedAreaIndicators = [],
    onClickOnCard = null,
    showPlanAreaPath = true,
    linkablePlanAreaPath = true,
    moreButtonSettings = null,
    showIndicatorWeight = true,
    showStatus = true,
    showTrend = true,
    showLastSample = false,
    className = null }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const navigate = useNavigate();
    const [currentObjective, setCurrentObjective] = useState(
        areaIndicator && areaIndicator.objectiveDTOs ? getCurrentObjective(areaIndicator.objectiveDTOs) : null
    );

    const handleClickOnName = () => {
        if (!allowClickOnName) return;
        navigate(`/plans/plans-explorer/${areaIndicator.area.plan.planUUID}?selectedAreaId=${encodeURIComponent(areaIndicator.area.areaId)}&selectedAreaIndicatorId=${encodeURIComponent(areaIndicator.areaIndicatorId)}`);
    }

    const handleClickOnCard = () => {
        if (!allowClickOnCard) return;
        if (onClickOnCard) onClickOnCard(areaIndicator);
    }

    if (!areaIndicator) return null;

    const progress = areaIndicator.progress;
    const status = areaIndicator.status;
    const trend = areaIndicator.trend;

    const highlightCard = highlightedAreaIndicators.some(indicator =>
        indicator.planUUID === areaIndicator.area.plan.planUUID &&
        indicator.planAreaId === areaIndicator.area.areaId &&
        indicator.areaIndicatorId === areaIndicator.areaIndicatorId
    );

    // only will show last sample if indicatorDTOType is QUANTITATIVE
    const showLastSampleFinal = showLastSample && areaIndicator.indicatorDTO.indicatorDTOType === 'QUANTITATIVE';

    // console.log('areaIndicator', areaIndicator);
    // console.log('highlightedAreaIndicators', highlightedAreaIndicators);
    // console.log('highlightCard', highlightCard);

    return (
        <Card variant='outlined'
            className={`${generateClassName('AreaIndicatorMiniCard-container', '', className)} ${allowClickOnCard ? 'clickable' : ''} ${highlightCard ? 'highlighted' : ''}`}
            onClick={handleClickOnCard}
        >
            <CardHeader title={areaIndicator.areaIndicatorName} className='AreaIndicatorMiniCard-header' onClick={allowClickOnName ? handleClickOnName : null} />
            <CardContent className='AreaIndicatorMiniCard-content'>
                <Box className='AreaIndicatorMiniCard-main-cointainer' width={'100%'} display='flex' flexDirection='row'>
                    {showLastSampleFinal && (
                        <Box width={'25%'} alignSelf={'center'}>
                            <AreaIndicatorLastSample areaIndicatorData={areaIndicator} />
                        </Box>
                    )}
                    <Box width={(showLastSampleFinal) ? '75%' : '100%'} alignSelf={'center'} display={'flex'} flexDirection={'row'} justifyContent={'center'} alignItems={'center'}>
                        {/* <Box className='AreaIndicatorMiniCard-indicator-and-weight' width={'100%'}> */}
                            <IndicatorUnitFactory indicator={areaIndicator.indicatorDTO} showIcon={true} className='AreaIndicatorMiniCard-indicator-unit' />
                            <ProgressFactory progressApproach='SIMPLE'
                                targetProgress={progress ? progress : 0}
                                timeProgress={(showCalendarProgress && currentObjective) ?
                                    calculateTimeProgress(currentObjective.startDate, currentObjective.endDate) : 0}
                                showCalendarProgress={showCalendarProgress}
                                showTargetProgress={showTargetProgress}
                                className='AreaIndicatorMiniCard-progress'
                            />
                            {showIndicatorWeight && (
                                <div className='AreaIndicatorMiniCard-weight'>
                                    <WeightRatingField weight={areaIndicator.areaIndicatorWeight} />
                                </div>
                            )}
                        {/* </Box> */}
                    </Box>
                </Box>
                {/* ABSOLUTE POSITION */}
                {areaIndicator.area && areaIndicator.area.plan && showPlanAreaPath &&
                    (<PlanAreaPath plan={areaIndicator.area.plan} area={areaIndicator.area} linkable={linkablePlanAreaPath} className='AreaIndicatorMiniCard-plan-area-path' />)
                }
                <IndicatorImpact frameworkSuite={'generic'} frameworkId={2} indicator={areaIndicator.indicatorDTO} className={'AreaIndicatorMiniCard-impact'} />
                <StatusTrendFactory approach='COMBINED' status={status} trend={trend}
                    showStatus={showStatus} showTrend={showTrend}
                    className={`AreaIndicatorMiniCard-status-and-trend ${moreButtonSettings ? 'move-further-to-left' : ''}`}
                />
            </CardContent>
            {moreButtonSettings && (
                <MoreButton icon={moreButtonSettings.icon || <MoreVert />}
                    buttons={moreButtonSettings.buttons.map(button => ({
                        ...button,
                        onClick: () => button.onClick({
                            areaIndicatorId: areaIndicator.areaIndicatorId,
                            planAreaId: areaIndicator.area.areaId,
                            planUUID: areaIndicator.area.plan.planUUID,

                        })
                    }))}
                    dropPositionAlign={moreButtonSettings.dropPositionAlign || 'right'}
                    rolesAllowed={moreButtonSettings.rolesAllowed || ['ADMIN', 'EDITOR', 'USER']}
                    className={moreButtonSettings.className || 'jb01-more-button'}
                />
            )}
        </Card>
    );
};

export default AreaIndicatorMiniCard;